<!-- eslint-disable vuejs-accessibility/no-autofocus -->
<template>
  <custom-dialog
    id="context-key-menu-dialog"
    v-model="contextKeyMenuDialog"
    v-model:fullscreen="isExpanded"
    show-resize-button
    show-chip
    :heading="editedItem.key"
    :subheading="editedItem.tags.includes('Variable') ? 'Variable' : 'Context Key'"
    :value-required="editedItem.valueRequired"
  >
    <template #custom-dialog-content>
      <v-container v-if="!isGroupAdmin" fluid>
        <v-row v-if="editedItem.tags.includes('Variable')">
          <v-col cols="12">
            <instructions-card :edited-item="editedItem"></instructions-card>
            <v-select
              v-if="!editedItem.possibleValues"
              v-model="editedItem.md"
              :items="['Yes', 'No']"
              label="Value"
            ></v-select>
            <v-select
              v-else
              v-model="editedItem.md"
              :items="editedItem.possibleValues"
              label="Value"
            ></v-select>
          </v-col>
        </v-row>
        <v-row
          v-if="!editedItem.tags.includes('Variable') && contextKeyMenuDialog == true"
          class="fill-height"
        >
          <v-col cols="12">
            <instructions-card :edited-item="editedItem"></instructions-card>
            <c-e-content-editor-tip-tap
              id="context-library-context-key-dialog"
              v-model="editedItem.output"
              :support-image="true"
              :image-layouts="ALIGN_ONLY_IMAGE_LAYOUTS"
              :use-placeholder="editedItem.usePlaceholder"
              :placeholder-text="editedItem.placeholder"
              :has-toolbar="true"
            ></c-e-content-editor-tip-tap>
            <v-checkbox
              id="use-placeholder-checkbox"
              v-model="editedItem.usePlaceholder"
              label="Apply Placeholder Text as Value"
              color="rgb(var(--v-theme-info))"
              hide-details="true"
              density="compact"
            ></v-checkbox>

            <v-card variant="text" class="location-card">
              <v-card-item class="pl-0">
                <v-card-title> Location </v-card-title>
                <template #prepend>
                  <v-icon size="25px">{{ 'mdi-map-marker-outline' }}</v-icon>
                </template>
              </v-card-item>
              <v-card-text class="pa-0">
                Note: The table below shows where this context key or variable is used across your
                site and/or child sites.
              </v-card-text>
            </v-card>
            <v-data-table
              :id="`context-key-details-dialog--module-data-table-${viewedItem.key}`"
              :headers="detailsHeaders"
              :items="viewedItem.modulesData"
              :loading="false"
              :items-per-page="5"
              :items-per-page-options="itemPerPageOptions"
              class="text-grey-darken-1 text-body-2 context-key-site-usage-table"
              :sort-by="[{ key: 'moduleDisplayName', order: 'asc' }]"
              fixed-header
              hover
            >
              <template #item="{ item }">
                <tr>
                  <td>{{ item.moduleDisplayName }}</td>
                  <td>
                    <a
                      :id="`context-key-details-dialog--${viewedItem.key}-module-${item.moduleId}-page-${item.pagePath}-link`"
                      :href="getPagePath(item.moduleId, item.pagePath)"
                      target="_blank"
                      class="csFormDialog-link"
                    >
                      {{ item.pageDisplayName }}
                    </a>
                  </td>
                  <td>
                    {{ item.numberOfOccurrences }}
                  </td>
                </tr>
              </template>
              <template #no-data>
                <p>There are no modules using {{ viewedItem.key }}</p>
              </template>
            </v-data-table>
          </v-col>
        </v-row>

        <v-card-actions>
          <v-container fluid class="d-flex flex-row justify-end align-center">
            <cs-button
              id="preferences-context--close-btn"
              class="mr-2"
              label="Cancel"
              @click="close"
            ></cs-button>
            <cs-button
              id="preferences-context--save-btn"
              label="Update"
              :disabled="
                isImageLoadInProgress ||
                (isContextKeyPreferencesEnabled() && editedItem.updateChildSites === null)
              "
              primary
              @click="save"
            ></cs-button>
          </v-container>
        </v-card-actions>
      </v-container>

      <!-- step 1 -->
      <v-stepper v-else v-model="stepper" tile flat>
        <v-stepper-header>
          <v-stepper-item title="Update Type" :value="1" :complete="stepper > 1" color="primary" />
        </v-stepper-header>

        <v-stepper-window v-show="stepper == 1" direction="vertical">
          <v-stepper-window-item :value="1">
            <v-card-text>
              <v-container fluid>
                <v-row class="fill-height">
                  <v-col :cols="columnSize" class="context-key-preferences">
                    <p><b>Please select your preferred option:</b></p>
                    <v-radio-group
                      v-model="editedItem.updateChildSites"
                      data-testid="context-library--update-preference-radio-group"
                    >
                      <v-radio
                        id="this-site-only"
                        :color="editedItem.updateChildSites ? 'secondary' : 'primary'"
                        label="This site and/or all child sites"
                        :value="false"
                      ></v-radio>
                      <v-radio
                        v-if="!editedItem.groupLocked"
                        id="individual-child-site"
                        :color="editedItem.updateChildSites ? 'primary' : 'secondary'"
                        label="Individual child sites"
                        :value="true"
                      ></v-radio>
                    </v-radio-group>
                    <v-select
                      v-if="editedItem.updateChildSites"
                      id="select-update-type"
                      v-model="childSiteUpdateType"
                      color="rgb(var(--v-theme-info))"
                      :items="getUpdateOptions()"
                      :label="childSiteUpdateType ? 'Update Type' : 'Select an Update Type'"
                      variant="underlined"
                    ></v-select>
                  </v-col>
                </v-row>
              </v-container>

              <v-container fluid class="d-flex flex-row justify-end align-center">
                <cs-button class="mr-2" label="Cancel" @click="close"></cs-button>
                <cs-button
                  label="Next"
                  primary
                  :disabled="!isNextButtonEnabled"
                  @click="stepperNext"
                ></cs-button>
              </v-container>
            </v-card-text>
          </v-stepper-window-item>
        </v-stepper-window>
        <v-divider></v-divider>

        <!-- step 2 -->
        <v-stepper-header>
          <v-stepper-item
            v-if="!editedItem.updateChildSites"
            title="Update Content"
            :value="2"
            :complete="stepper > 2"
            color="primary"
          />
          <v-stepper-item
            v-else
            title="Select Child and Value to Update"
            :value="2"
            :complete="stepper > 2"
            color="primary"
          />
        </v-stepper-header>

        <v-stepper-window v-show="stepper == 2" direction="vertical">
          <v-stepper-window-item :value="2">
            <v-card-text v-if="!editedItem.updateChildSites">
              <v-container fluid class="context-key-editing-container">
                <v-row v-if="!editedItem.tags.includes('Variable')" class="fill-height">
                  <v-col cols="12">
                    <instructions-card :edited-item="editedItem"></instructions-card>
                    <c-e-content-editor-tip-tap
                      id="page-context-key-dialog"
                      ref="contentEditor"
                      v-model="editedItem.output"
                      :support-image="true"
                      :image-layouts="ALIGN_ONLY_IMAGE_LAYOUTS"
                      :use-placeholder="editedItem.usePlaceholder"
                      :placeholder-text="editedItem.placeholder"
                      :has-toolbar="true"
                    ></c-e-content-editor-tip-tap>
                    <v-checkbox
                      id="use-placeholder-checkbox"
                      v-model="editedItem.usePlaceholder"
                      label="Apply Placeholder Text as Value"
                      color="rgb(var(--v-theme-info))"
                      hide-details="true"
                      density="compact"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <v-row v-if="editedItem.tags.includes('Variable')" class="fill-height">
                  <v-col :cols="columnSize">
                    <instructions-card :edited-item="editedItem"></instructions-card>
                    <v-select
                      v-if="!editedItem.possibleValues"
                      v-model="editedItem.md"
                      :items="['Yes', 'No']"
                      label="Value"
                      variant="underlined"
                      color="rgb(var(--v-theme-info))"
                    ></v-select>
                    <v-select
                      v-if="editedItem.possibleValues"
                      v-model="editedItem.md"
                      :items="editedItem.possibleValues"
                      label="Value"
                      variant="underlined"
                      color="rgb(var(--v-theme-info))"
                    ></v-select>
                  </v-col>
                </v-row>
              </v-container>

              <v-container v-if="!editedItem.updateChildSites" fluid>
                <v-checkbox
                  v-model="editedItem.groupLocked"
                  label="Group Lock"
                  color="rgb(var(--v-theme-info))"
                  density="compact"
                ></v-checkbox>
                <v-container fluid>
                  <v-row v-if="isContextKeyPreferencesEnabled()" class="fill-height">
                    <v-col cols="12" class="context-key-preferences pl-0 pt-8">
                      <p><b>Please select your preferred option:</b></p>
                      <v-radio-group
                        id="context-library--update-child-sites-radio-group"
                        v-model="editedItem.updateAllChildSites"
                      >
                        <v-radio
                          id="context-library--update-child-sites-only-radio-group--false"
                          :color="!editedItem.updateAllChildSites ? 'primary' : 'secondary'"
                          label="Apply change to this site only."
                          :value="false"
                        ></v-radio>
                        <v-radio
                          id="context-library--update-all-child-sites-radio-group--true"
                          :color="editedItem.updateAllChildSites ? 'primary' : 'secondary'"
                          label="Apply change to this site and all child sites."
                          :value="true"
                        ></v-radio>
                      </v-radio-group>
                      <p>
                        <b>Note:</b> If you choose the second option, the current value for this
                        context key on your child sites will be replaced with the new value.
                      </p>
                    </v-col>
                  </v-row>
                </v-container>
              </v-container>
              <v-container fluid class="d-flex flex-row justify-end align-center">
                <cs-button class="mr-2" label="Back" @click="stepperBack"></cs-button>
                <cs-button
                  id="preferences-context--save-btn"
                  label="Update"
                  primary
                  :disabled="
                    isImageLoadInProgress ||
                    (isContextKeyPreferencesEnabled() && editedItem.updateAllChildSites === null)
                  "
                  @click="save"
                ></cs-button>
              </v-container>
            </v-card-text>

            <v-card-text v-if="editedItem.updateChildSites">
              <context-key-filter
                id="child-context--filter"
                :filter="childrenContextKeyFilter"
                :set-filter="setChildrenContextKeyFilter"
                :items="contextChildList"
              />
              <v-data-table
                id="child-context--data-table"
                ref="childrenContextKeyTable"
                v-model="selectedUpdateChild"
                :headers="childEditHeaders"
                :items="contextChildList"
                :items-per-page="5"
                :items-per-page-options="itemPerPageOptions"
                show-select
                checkbox-color="primary"
                fixed-header
                :sort-by="[{ key: 'clientName', order: 'asc' }]"
                :loading="false"
                :search="childrenContextKeySearchValue"
                :custom-filter="childrenContextKeyFilterHandler"
                must-sort
                hover
                return-object
              >
                <template #top>
                  <div class="pl-5">{{ selectedUpdateChild.length }} row(s) selected</div>
                </template>
                <template #item.contextKeyValue="{ item }">
                  <td class="text-left" v-html="mdToHtml(item.contextKeyValue)"></td>
                </template>
                <template #item.applyValue="{ item }">
                  <v-icon
                    v-if="!editedItem.tags.includes('Variable') || !item.contextKeyValue == ``"
                    class="custom-icon"
                    style="transition: none; margin-top: -5%"
                    @click="copyContextValueToClipboard(item.contextKeyValue)"
                    >mdi-content-copy</v-icon
                  >
                </template>
              </v-data-table>
              <!-- end contextChildList table -->
              <v-card-actions>
                <v-container fluid class="d-flex flex-row justify-end align-center">
                  <cs-button class="mr-2" label="Back" @click="stepperBack"></cs-button>
                  <cs-button
                    label="Next"
                    primary
                    :disabled="selectedUpdateChild.length === 0"
                    @click="stepperNext"
                  ></cs-button>
                </v-container>
              </v-card-actions>
            </v-card-text>
          </v-stepper-window-item>
        </v-stepper-window>
        <v-divider></v-divider>

        <!-- step 3 -->
        <v-stepper-header v-if="editedItem.updateChildSites">
          <v-stepper-item
            v-if="childSiteUpdateType === 'Update Both' || childSiteUpdateType === 'Update Value'"
            title="Update Content"
            :value="3"
            :complete="stepper > 3"
            color="primary"
          />
          <v-stepper-item
            v-else
            title="Update Lock Status"
            :value="3"
            :complete="stepper > 3"
            color="primary"
          />
        </v-stepper-header>

        <v-stepper-window v-show="stepper == 3" direction="vertical">
          <v-stepper-window-item :value="3">
            <v-container
              v-if="childSiteUpdateType === 'Update Lock Status'"
              fluid
              class="d-flex justify-space-between align-center"
            >
              <v-col :cols="columnSize">
                <v-select
                  v-if="
                    childSiteUpdateType === 'Update Both' ||
                    childSiteUpdateType === 'Update Lock Status'
                  "
                  id="select-lock-status"
                  v-model="selectedChildGroupLock"
                  color="rgb(var(--v-theme-info))"
                  :items="childGroupLock"
                  item-title="text"
                  item-value="value"
                  label="Lock Status"
                  variant="underlined"
                ></v-select>
              </v-col>
            </v-container>

            <v-container
              v-if="
                editedItem.updateChildSites &&
                (childSiteUpdateType === 'Update Both' || childSiteUpdateType === 'Update Value')
              "
              fluid
              class="context-key-editing-container"
            >
              <v-col cols="12" class="pa-0">
                <instructions-card :edited-item="editedItem"></instructions-card>
                <c-e-content-editor-tip-tap
                  v-if="!editedItem.tags.includes('Variable') && stepper === 3"
                  id="context-library-context-key-dialog"
                  v-model="editedItem.output"
                  :support-image="true"
                  :image-layouts="ALIGN_ONLY_IMAGE_LAYOUTS"
                  :use-placeholder="editedItem.usePlaceholder"
                  :placeholder-text="editedItem.placeholder"
                  :has-toolbar="true"
                ></c-e-content-editor-tip-tap>
                <v-checkbox
                  v-if="!editedItem.tags.includes('Variable') && stepper === 3"
                  id="use-placeholder-checkbox"
                  v-model="editedItem.usePlaceholder"
                  label="Apply Placeholder Text as Value"
                  color="rgb(var(--v-theme-info))"
                  hide-details="true"
                  density="compact"
                ></v-checkbox>
              </v-col>
              <v-row v-if="editedItem.tags.includes('Variable')" class="pa-0">
                <v-col :cols="columnSize">
                  <v-select
                    v-if="editedItem.tags.includes('Variable') && !editedItem.possibleValues"
                    v-model="editedItem.md"
                    :items="['Yes', 'No']"
                    label="Value"
                    variant="underlined"
                    color="rgb(var(--v-theme-info))"
                  ></v-select>
                  <v-select
                    v-if="editedItem.tags.includes('Variable') && editedItem.possibleValues"
                    v-model="editedItem.md"
                    :items="editedItem.possibleValues"
                    label="Value"
                    variant="underlined"
                    color="rgb(var(--v-theme-info))"
                  ></v-select>
                </v-col>
              </v-row>
            </v-container>

            <v-container fluid class="d-flex flex-row justify-end align-center">
              <cs-button
                id="preferences-context--close-btn"
                class="mr-2"
                label="Back"
                @click="stepperBack"
              ></cs-button>

              <cs-button
                v-if="
                  childSiteUpdateType === 'Update Lock Status' ||
                  childSiteUpdateType === 'Update Value'
                "
                id="preferences-context--save-btn"
                label="Next"
                primary
                @click="stepperNext"
              ></cs-button>

              <cs-button
                v-if="childSiteUpdateType === 'Update Both'"
                id="preferences-context--save-btn"
                label="Next"
                primary
                @click="stepperNext"
              ></cs-button>
            </v-container>
          </v-stepper-window-item>
        </v-stepper-window>
        <v-divider></v-divider>

        <!-- step 4 -->
        <v-stepper-header v-if="editedItem.updateChildSites">
          <v-stepper-item
            v-if="childSiteUpdateType === 'Update Both'"
            title="Update Lock Status"
            :value="4"
            :complete="stepper > 4"
            color="primary"
          />
          <v-stepper-item
            v-else
            title="Confirm Update - The value and/or lock status will be updated for your site and/or selected child sites when you press Update."
            :value="4"
            color="primary"
            class="custom-button"
          />
        </v-stepper-header>

        <v-stepper-window
          v-if="childSiteUpdateType === 'Update Both'"
          v-show="stepper == 4"
          direction="vertical"
        >
          <v-stepper-window-item :value="4">
            <v-container fluid class="d-flex justify-space-between align-center">
              <v-col :cols="columnSize">
                <v-select
                  id="select-lock-status"
                  v-model="selectedChildGroupLock"
                  :items="childGroupLock"
                  item-title="text"
                  item-value="value"
                  label="Lock Status"
                  variant="underlined"
                  color="rgb(var(--v-theme-info))"
                ></v-select>
              </v-col>
            </v-container>
            <v-container fluid class="d-flex flex-row justify-end align-center">
              <cs-button
                id="preferences-context--close-btn"
                class="mr-2"
                label="Back"
                @click="stepperBack"
              ></cs-button>

              <cs-button
                id="preferences-context--save-btn"
                label="Next"
                primary
                @click="stepperNext"
              ></cs-button>
            </v-container>
          </v-stepper-window-item>
        </v-stepper-window>

        <v-stepper-window v-else v-show="stepper == 4" direction="vertical">
          <v-stepper-window-item :value="4">
            <v-container fluid>
              <p>
                <br />
                <b>Note:</b> The table below shows where this context key or variable is used across
                your site and/or child sites. <br />
              </p>
              <v-data-table
                :id="`context-key-details-dialog--module-data-table-${viewedItem.key}`"
                :headers="detailsHeaders"
                :items="viewedItem.modulesData"
                :loading="false"
                :items-per-page="5"
                :items-per-page-options="itemPerPageOptions"
                class="text-grey-darken-1 text-body-2 context-key-site-usage-table"
                :sort-by="[{ key: 'moduleDisplayName', order: 'asc' }]"
                fixed-header
                hover
              >
                <template #item="{ item }">
                  <tr>
                    <td>{{ item.moduleDisplayName }}</td>
                    <td>
                      <a
                        :id="`context-key-details-dialog--${viewedItem.key}-module-${item.moduleId}-page-${item.pagePath}-link`"
                        :href="getPagePath(item.moduleId, item.pagePath)"
                        target="_blank"
                        class="csFormDialog-link"
                      >
                        {{ item.pageDisplayName }}
                      </a>
                    </td>
                    <td>
                      {{ item.numberOfOccurrences }}
                    </td>
                  </tr>
                </template>
                <template #no-data>
                  <p>There are no modules using {{ viewedItem.key }}.</p>
                </template>
              </v-data-table>
              <!-- end viewedItem.modulesData table-->
            </v-container>
            <v-container fluid class="d-flex flex-row justify-end align-center">
              <cs-button
                id="preferences-context--close-btn"
                class="mr-2"
                label="Back"
                @click="stepperBack"
              ></cs-button>

              <cs-button
                id="preferences-context--save-btn"
                label="Update"
                primary
                @click="saveChildContext"
              ></cs-button>
            </v-container>
          </v-stepper-window-item>
        </v-stepper-window>
        <v-divider></v-divider>

        <!-- step 5 -->
        <v-stepper-header>
          <v-stepper-item
            v-if="editedItem.updateChildSites && childSiteUpdateType === 'Update Both'"
            title="Confirm Update - The value and/or lock status will be updated for your site and/or selected child sites
            when you press Update."
            :value="5"
            :complete="stepper > 5"
            color="primary"
            class="custom-button"
          />
        </v-stepper-header>

        <v-stepper-window v-show="stepper == 5" direction="vertical">
          <v-stepper-window-item :value="5">
            <v-container fluid class="d-flex justify-space-between align-center">
              <v-container fluid>
                <p>
                  <br />
                  <b>Note:</b> The table below shows where this context key or variable is used
                  across your site and/or child sites. <br />
                </p>
                <v-data-table
                  :id="`context-key-details-dialog--module-data-table-${viewedItem.key}`"
                  :headers="detailsHeaders"
                  :items="viewedItem.modulesData"
                  :loading="isSiteUsageTableLoading ? 'primary' : false"
                  :items-per-page="5"
                  :items-per-page-options="itemPerPageOptions"
                  class="text-grey-darken-1 text-body-2 context-key-site-usage-table"
                  :sort-by="[{ key: 'moduleDisplayName', order: 'asc' }]"
                  fixed-header
                  hover
                >
                  <template #item="{ item }">
                    <tr>
                      <td>{{ item.moduleDisplayName }}</td>
                      <td>
                        <a
                          :id="`context-key-details-dialog--${viewedItem.key}-module-${item.moduleId}-page-${item.pagePath}-link`"
                          :href="getPagePath(item.moduleId, item.pagePath)"
                          target="_blank"
                          class="csFormDialog-link"
                        >
                          {{ item.pageDisplayName }}</a
                        >
                      </td>
                      <td>
                        {{ item.numberOfOccurrences }}
                      </td>
                    </tr>
                  </template>
                  <template #no-data>
                    <p>There are no modules using {{ viewedItem.key }}.</p>
                  </template>
                </v-data-table>
                <!-- end 2 viewedItem.modulesData table -->
              </v-container>
            </v-container>
            <v-container fluid class="d-flex flex-row justify-end align-center">
              <cs-button
                id="preferences-context--close-btn"
                class="mr-2"
                label="Back"
                @click="stepperBack"
              ></cs-button>

              <cs-button
                id="preferences-context--save-btn"
                label="Update"
                primary
                :disabled="
                  isImageLoadInProgress ||
                  selectedUpdateChild.length === 0 ||
                  childSiteUpdateType === null
                "
                @click="saveChildContext"
              ></cs-button>
            </v-container>
          </v-stepper-window-item>
        </v-stepper-window>
      </v-stepper>
    </template>
  </custom-dialog>
</template>

<script>
import TurndownService from 'turndown';
import { mapState, mapGetters } from 'vuex';
import { CSBase } from '@complispace/cs-design-system';
import markdownIt from 'markdown-it';
import { markdownItFancyListPlugin } from 'markdown-it-fancy-lists';
import InstructionsCard from '@/components/content/InstructionsCard';
import DynamicContent from '@/components/content/DynamicContent';
import CEContentEditorTipTap from '@/components/content/CEContentEditorTipTap';
import { ALIGN_ONLY_IMAGE_LAYOUTS } from '@/components/content/tiptap/imageWithLayout';
import CustomDialog from '@/components/custom/CustomDialog';
import { getContextKeySubstitutedTitle, isContextKeyGroupLocked } from '@/lib/util';
import updateTableColumnsWidth from '@/lib/tableUtils';
import EventBus from '@/bus';
import {
  KEEP_MARKDOWN_TAGS,
  MIXPANEL_TRACE_EVENT,
  MIXPANEL_TRACE_STRING,
  ITEM_PER_PAGE_OPTIONS_FROM_5
} from '@/constants';
import { collectingImagesFromContent, changeContentImageSrc } from '@/lib/strings';
import * as types from '@/store/mutationTypes';
import { plugInAdapter } from '@/lib/globalPlugIns';
import { context } from '@/api';
import ContextKeyFilter from '@/components/content/filter-input/ContextKeyFilter';
import { defaultContextKeyFilter } from '@/components/content/filter-input/util';
import TempPage from '../views/TempPage';

import nunjucks from 'nunjucks';
const md = markdownIt({
  html: true
});

md.use(markdownItFancyListPlugin);

// NOTE: Turning off autoescaping can be dangerous
nunjucks.configure('./', { autoescape: false });

export default {
  name: 'ContextMenuDialog',

  components: {
    TempPage,
    DynamicContent,
    CEContentEditorTipTap,
    CustomDialog,
    ContextKeyFilter,
    InstructionsCard
  },
  extends: CSBase,

  props: {},

  data() {
    return {
      ALIGN_ONLY_IMAGE_LAYOUTS,
      childEditHeaders: [
        {
          title: 'Name',
          align: 'left',
          key: 'clientName',
          value: 'clientName',
          sortable: true
        },
        {
          title: 'Value',
          align: 'left',
          key: 'contextKeyValue',
          value: 'contextKeyValue',
          sortable: true
        },
        {
          title: 'Copy Value',
          align: 'center',
          key: 'applyValue',
          value: 'applyValue',
          sortable: false
        },
        {
          title: 'Child Lock',
          align: 'left',
          key: 'isChildSiteLocked',
          value: 'isChildSiteLocked',
          sortable: true
        },
        {
          title: 'Status',
          align: 'center',
          key: 'childUpdateStatus',
          value: 'childUpdateStatus',
          sortable: true
        }
      ],
      detailsHeaders: [
        {
          title: 'Module Name',
          align: 'left',
          key: 'moduleDisplayName',
          value: 'moduleDisplayName',
          sortable: true
        },
        {
          title: 'Page Title',
          align: 'left',
          key: 'pageDisplayName',
          value: 'pageDisplayName',
          sortable: false
        },
        {
          title: 'Number of Occurrences',
          align: 'left',
          key: 'numberOfOccurrences',
          value: 'numberOfOccurrences',
          sortable: false
        }
      ],
      contentLoad: false,
      editedItem: {
        key: '',
        md: '',
        html: '',
        tags: [],
        placeholder: '',
        possibleValues: [],
        updateChildSites: null,
        updateAllChildSites: null,
        groupLocked: false,
        output: null,
        valueRequired: false,
        instructions: '',
        usePlaceholder: false
      },
      defaultItem: {
        key: '',
        md: '',
        html: '',
        tags: [],
        placeholder: '',
        possibleValues: [],
        updateChildSites: null,
        updateAllChildSites: null,
        groupLocked: false,
        output: null,
        valueRequired: false,
        instructions: '',
        usePlaceholder: false
      },
      isImageLoadInProgress: false,
      isEditingChildModal: true,
      contextChildList: [],
      selectedUpdateChild: [],
      childSiteUpdateType: null,
      childSiteLockFlag: null,
      stepper: 1,
      contextKeyMenuDialog: false,
      viewedItem: {},
      isSiteUsageTableLoading: false,
      childGroupLock: [
        { text: 'No', value: false },
        { text: 'Yes', value: true }
      ],
      selectedChildGroupLock: false,
      childrenContextKeyFilter: defaultContextKeyFilter,
      localClipboard: null,
      itemPerPageOptions: ITEM_PER_PAGE_OPTIONS_FROM_5,
      isExpanded: false
    };
  },

  computed: {
    ...mapState({
      client: (state) => state.client,
      subdomain: (state) => state.session.subdomain,
      role: (state) => state.authorisation.role,
      siteType: (state) => state.authorisation.siteType,
      isLoadingImages: (state) => state.appearance.isLoadingImages
    }),

    ...mapGetters({
      contextItemValues: 'clientContext/values',
      previousContextItemValues: 'clientContext/previousValues',
      contextItems: 'clientContext/items',
      isAllowedTo: 'authorisation/isAllowedTo',
      hasAccess: 'authorisation/hasAccess',
      getAddedImages: 'imageUploader/getAddedImages',
      appName: 'app/name',
      isAdminDraftView: 'authorisation/isAdminDraftView',
      viewOnlyContextKeyValues: 'clientContext/viewOnlyContextKeyValues',
      isContextKeyPreferencesEnabledForClient: 'client/isContextKeyPreferencesEnabled',
      getXSiteUrl: 'session/getXSiteURL'
    }),
    childrenContextKeySearchValue() {
      return JSON.stringify(this.childrenContextKeyFilter);
    },
    isGroupAdmin() {
      const roleId = parseInt(this.role, 10);
      if (
        (roleId === 3 || roleId === 4) &&
        this.siteType === 3 &&
        (this.editedItem.tags.includes('Variable') || this.editedItem.tags.includes('Merge Field'))
      ) {
        return true;
      }
      return false;
    },

    isNextButtonEnabled() {
      return (
        this.editedItem.updateChildSites !== null &&
        (!this.editedItem.updateChildSites || this.childSiteUpdateType)
      );
    },

    columnSize() {
      return this.isExpanded ? '6' : '12';
    }
  },

  watch: {
    'editedItem.updateChildSites': {
      handler(to) {
        if (to) {
          this.getChildList();
          this.editedItem.output = null; // reset editor content when switching to child site update
          this.editedItem.usePlaceholder = false;
          return;
        }
        // reset editor content to current site value when switching to this site only update
        this.editedItem.output = this.editedItem.currentValue;
        this.editedItem.usePlaceholder = this.editedItem.currentUsePlaceholder;
      }
    },

    contextKeyMenuDialog(value) {
      if (!value) {
        this.close();
      }
    },

    isLoadingImages(value) {
      if (value) {
        this.isImageLoadInProgress = true;
        this.setLoading(true);
      } else {
        this.isImageLoadInProgress = false;
        this.setLoading(false);
      }
    },
    childrenContextKeyFilter() {
      this.selectedUpdateChild = []; // reset selected child sites when filter changes
    }
  },

  methods: {
    isContextKeyPreferencesEnabled() {
      return (
        !this.editedItem.groupLocked &&
        this.isGroupAdmin &&
        this.isContextKeyPreferencesEnabledForClient
      );
    },

    editPopup(key) {
      const item = this.contextItems.filter((it) => it.key.toLowerCase() === key.toLowerCase())[0];
      // child can't edit it
      if (isContextKeyGroupLocked(item, this.client)) {
        return;
      }
      if (this.hasAccess('singlecontexteditTable')) {
        this.editedItem.key = item.key;
        const contentValue = item.usePlaceholder ? item.placeholder : item.value;
        this.editedItem.currentValue = this.mdToHtml(contentValue);
        this.editedItem.output = this.mdToHtml(contentValue);
        this.editedItem.md = contentValue;
        this.editedItem.tags = item.tags;
        this.editedItem.placeholder = item.placeholder ? item.placeholder : '';
        this.editedItem.possibleValues = item.possibleValues;
        this.editedItem.groupLocked = !!item.ownerId;
        this.selectedChildGroupLock = !!item.ownerId;
        this.editedItem.updateChildSites = null;
        this.editedItem.updateAllChildSites = null;
        this.contextKeyMenuDialog = true;
        this.editedItem.valueRequired = item.valueRequired;
        this.editedItem.instructions = item.instructions;
        this.editedItem.currentUsePlaceholder = item.usePlaceholder;
        this.editedItem.usePlaceholder = item.usePlaceholder;
      }
      this.viewDetails(this.editedItem.key);
    },

    close() {
      this.contextKeyMenuDialog = false;
      this.childSiteUpdateType = null;
      this.childSiteLockFlag = null;
      this.selectedUpdateChild = [];
      this.restoreStepper();
      this.clearChildrenContextKeyFilter();
      setTimeout(() => {
        this.editedItem = { ...this.defaultItem };
      }, 300);
      if (this.getAddedImages().length) {
        this.$store.commit(types.IMAGE_UPLOADER_IMAGE_DELETE_ALL);
      }
    },

    mdToHtml(content) {
      if (!content) return null;

      if (content.includes('</th>') || content.includes('</td>'))
        return md.render(updateTableColumnsWidth(content));

      return md.render(content);
    },

    async save() {
      this.setLoading(true);

      try {
        let value = '';
        let valueHtml = '';

        if (this.editedItem.tags.includes('Variable')) {
          value = this.editedItem.md ? this.editedItem.md : '';
        } else {
          const turndownService = new TurndownService();
          // Keep below HTML tags in markdown.
          turndownService.addRule('keep', {
            filter: ['img'],
            replacement(contentString, node) {
              return node.outerHTML;
            }
          });
          turndownService.keep(KEEP_MARKDOWN_TAGS);

          if (this.editedItem.output && this.editedItem.output.length) {
            const images = collectingImagesFromContent(this.editedItem.output);

            if (images && images.length) {
              value = turndownService.turndown(changeContentImageSrc(this.editedItem.output));
              valueHtml = changeContentImageSrc(this.editedItem.output);
            } else {
              value = turndownService.turndown(this.editedItem.output);
              valueHtml = this.editedItem.output;
            }
          }
        }
        // NOTE: have to explicity set details object to strip any
        // Vue observer objects.
        const details = {
          key: this.editedItem.key,
          value,
          valueHtml,
          groupLocked: this.editedItem.groupLocked,
          usePlaceholder: this.editedItem.usePlaceholder,
          updateChildSites: this.isContextKeyPreferencesEnabled()
            ? this.editedItem.updateAllChildSites
            : false
        };
        await this.$store.dispatch('clientContext/updateContextItem', details);

        // Track Context Key Value Update event in Mixpanel.
        this.trackContextKeyValueUpdateEvent();

        this.$emit('refresh');
        EventBus.$emit('onContextKeyChangeFromPage');
      } catch (error) {
        this.showErrorResponseAlert(error);
      } finally {
        this.setLoading(false);
        this.close();
      }
    },

    restoreStepper() {
      this.stepper = 1;
    },

    stepperNext() {
      this.stepper += 1;
    },

    stepperBack() {
      this.stepper -= 1;
    },

    copyContextValueToClipboard(value) {
      this.editedItem.output = this.mdToHtml(value);
      this.showSuccessAlert('Value Copied', true);
      setTimeout(() => {
        this.clearAlert();
      }, 2000);
    },

    getUpdateOptions() {
      return ['Update Value', 'Update Lock Status', 'Update Both'];
    },

    async saveChildContext() {
      if (this.loading) return;
      this.setLoading(true);

      const details = {};

      try {
        let value = '';
        let valueHtml = '';

        switch (this.childSiteUpdateType) {
          case 'Update Both':
            details.updateType = 'All';
            break;
          case 'Update Lock Status':
            details.updateType = 'Lock';
            break;
          case 'Update Value':
            details.updateType = 'Context';
            break;
          default:
            details.updateType = 'All';
        }

        details.clientContextIds = this.selectedUpdateChild.map((item) => item.clientContextId);

        if (this.editedItem.tags.includes('Variable')) {
          value = this.editedItem.md ? this.editedItem.md : '';
        } else {
          const turndownService = new TurndownService();
          // Keep below HTML tags in markdown.
          turndownService.keep(KEEP_MARKDOWN_TAGS);
          turndownService.addRule('keep', {
            filter: ['img'],
            replacement(content, node) {
              return node.outerHTML;
            }
          });

          if (this.editedItem.output && this.editedItem.output.length) {
            const images = collectingImagesFromContent(this.editedItem.output);

            if (images && images.length) {
              value = turndownService.turndown(changeContentImageSrc(this.editedItem.output));
              valueHtml = changeContentImageSrc(this.editedItem.output);
            } else {
              value = turndownService.turndown(this.editedItem.output);
              valueHtml = this.editedItem.output;
            }
          }
        }
        // NOTE: have to explicity set details object to strip any
        // Vue observer objects.
        details.value = value;
        details.valueHtml = valueHtml;
        details.groupLocked = this.selectedChildGroupLock;
        details.usePlaceholder = this.editedItem.usePlaceholder;
        details.key = this.editedItem.key;
        await this.$store.dispatch('clientContext/updateChildContextItem', details);

        // Track Context Key Value Update event in Mixpanel.
        // this.trackContextKeyValueUpdateEvent();
      } catch (error) {
        this.showErrorResponseAlert(error);
        this.close();
      } finally {
        this.setLoading(false);
        this.close();
      }
    },

    async getChildList() {
      if (this.editedItem.key) {
        let list = [];

        try {
          const response = await context.getChildContext(this.editedItem.key);
          if (response.status === 200 && response.data && response.data.length) {
            list = this.appendDerivedAttributes(response.data);
          }
        } catch (err) {
          this.showErrorResponseAlert(err);
        } finally {
          this.contextChildList = list;
        }
      } else {
        this.contextChildList = [];
      }
    },
    appendDerivedAttributes(list) {
      return list.map((item) => {
        return {
          ...item,
          childUpdateStatus: item.contextKeyValue || item.usePlaceholder ? 'Complete' : 'Blank',
          isChildSiteLocked: item.ownerId ? 'Yes' : 'No'
        };
      });
    },
    setChildrenContextKeyFilter(newFilter) {
      this.childrenContextKeyFilter = newFilter;
    },
    clearChildrenContextKeyFilter() {
      this.childrenContextKeyFilter = defaultContextKeyFilter;
    },
    onToggleSelectAll({ value: isSelectingAll }) {
      if (isSelectingAll) {
        const currentFilteredItems =
          this.$refs.childrenContextKeyTable.$children?.[0]?.filteredItems;
        this.selectedUpdateChild = currentFilteredItems;
      } else {
        this.selectedUpdateChild = [];
      }
    },

    childrenContextKeyFilterHandler(_value, _query, item) {
      const newFilter = this.childrenContextKeyFilter;
      const isItemVisible = Object.keys(newFilter).every((filterKey) => {
        const filterValues = newFilter[filterKey];
        const itemValue = item?.value[filterKey];
        if (filterValues.length === 0) return true;
        if (filterKey === 'contextKeyValue' && filterValues.includes('')) {
          if (!itemValue || itemValue.trim() === '') return true;
        }
        return filterValues.includes(itemValue);
      });
      return isItemVisible;
    },
    async viewDetails(key) {
      if (key) {
        this.isSiteUsageTableLoading = true;
        let formattedContextKeyPageDetails = [];

        try {
          const response = await context.getPageDetailsForContextKey(key);

          if (response.status === 200 && response.data && response.data.length) {
            formattedContextKeyPageDetails = this.formatContextKeyPageDetails(response.data);
          }
        } catch (err) {
          this.isSiteUsageTableLoading = false;
          this.showErrorResponseAlert(err);
        } finally {
          this.isSiteUsageTableLoading = false;
          this.viewedItem = {
            key,
            modulesData: formattedContextKeyPageDetails
          };
        }
      } else {
        this.viewedItem = {};
      }
    },

    formatContextKeyPageDetails(pageDetails) {
      return pageDetails.map((detail) => {
        return {
          moduleId: detail.moduleId,
          moduleDisplayName: detail.moduleDisplayName
            ? getContextKeySubstitutedTitle(detail.moduleDisplayName, this.contextItemValues)
            : '',
          pageDisplayName: detail.pageDisplayName
            ? getContextKeySubstitutedTitle(detail.pageDisplayName, this.contextItemValues)
            : '',
          pagePath: detail.page,
          numberOfOccurrences: detail.numberOfOccurrences
        };
      });
    },

    getPagePath(moduleId, pagePath) {
      const isDevelopment = process.env.NODE_ENV === 'development';
      const xSiteUrl = isDevelopment
        ? `${this.getXSiteUrl().replace('https://', 'http://')}:${window.location.port}`
        : this.getXSiteUrl();
      return `${xSiteUrl}/module/${moduleId}/page/${pagePath}`;
    },

    trackContextKeyValueUpdateEvent() {
      try {
        const { plugIns } = this.$app;

        const properties = plugInAdapter.Adapter.getCustomMixpanelProperties({
          appName: this.appName,
          client: this.client,
          subdomain: this.subdomain,
          userRole: MIXPANEL_TRACE_STRING.USER_ROLE_VIEW.ADMIN,
          contextKeyName: this.editedItem.key
        });

        plugIns.mixPanelTrackEvent(MIXPANEL_TRACE_EVENT.CONTEXT_KEY_VALUE_UPDATE, properties);
      } catch (error) {
        console.log('Mixpanel Error: ', error);
      }
    }
  }
};
</script>

<style scoped>
.create-dialog button {
  text-transform: none;
}
.custom-icon {
  color: rgba(0, 0, 0, 0.2); /* Default color: light grey (neutral/20) */
}

.custom-icon:hover,
.custom-icon:active {
  color: rgba(0, 0, 0, 1); /* Hover/mouse down color: Neutral/100 */
}

td a.csFormDialog-link {
  color: rgb(var(--v-theme-info));
}

.v-stepper-header {
  box-shadow: none;
  display: flex;
  justify-content: flex-start;
}

.custom-button {
  width: 100%;
  text-align: left;
}
.context-key-site-usage-table {
  margin-bottom: 2%;
}
.context-key-site-usage-table :deep(.v-data-table-footer) {
  order: -1;
}
.v-card-text,
.v-container {
  padding-bottom: 0;
  padding-top: 0;
}
.v-window {
  margin-top: 0;
}

.v-checkbox {
  display: flex;
  justify-content: flex-start;
}

.v-card-title {
  font-size: 16px;
}

.location-card {
  border-top: 1px solid #e0e0e0;
  margin: 1rem 0;
}
</style>
<style>
#child-context--data-table td {
  vertical-align: top;
  padding-top: 0.5rem;
}

#child-context--data-table p,
#child-context--data-table body {
  font-size: 0.875rem;
}

#child-context--data-table .v-checkbox-btn .mdi-checkbox-marked.v-icon {
  color: rgb(var(--v-theme-info));
}
</style>
